/****************************************************
 * Module Name:  cancelledStoneReport.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { useState, useEffect } from "react";
import { Box, Tooltip, Button } from "@mui/material";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import Constants from "../../utility/constants";
import Utility from "../../utility/utility";
import CommonSnackbar from "../common/commonSnackBar";
import { DataGridPro, GridToolbar } from "@mui/x-data-grid-pro";
import dayjs from "dayjs";
import ReportProxy from "../../utility/proxy/manager/reportProxy";
import renderCellExpand from "../common/dataGridCellExpand";
import StoneDetailModal from "../common/stoneDetailModal";
import ViewSellMemoModal from "../operations/viewSellMemoModal";


const CancelledStoneReport = ({ invoiceDate }) => {

    const [data, setData] = useState([]);
    const [loader, setLoader] = useState();

    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarVariant, setSnackbarVariant] = useState("");

    const [stoneId, setStoneId] = useState("");
    const [showStoneDetailModal, setShowStoneDetailModal] = useState(false);

    const [noOfStones, setNoOfStones] = useState(0);
    const [totalSize, setTotalSize] = useState(0);
    const [totalPrice, setTotalPrice] = useState(0);
    const [avgPrice, setAvgPrice] = useState(0);
    const [avgDiscount, setAvgDiscount] = useState(0);
    const [selectedInvoiceId, setSelectedInvoiceId] = useState("");
    const [showViewSellModal, setShowViewSellModal] = useState(false);

    const columns = [
        {
            field: "invoiceid",
            headerName: "INVOICEID",
            headerAlign: "center",
            align: "center",
            width: 80,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.NeedToShowInvoiceDetailModal() ?
                <Button
                    sx={{ textDecorationLine: "underline", color: 'black', }}
                    onClick={() => handleInvoiceDetailModal(params)}
                >
                    {params.value}
                </Button>
                :
                <div>{params.value}</div>,
        },
        {
            field: "stoneid",
            headerName: "STONEID",
            headerAlign: "center",
            align: "center",
            width: 70,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.NeedToShowStoneDetailModal() ?
                <Button
                    sx={{ textDecorationLine: "underline" }}
                    onClick={() => handleStoneDetailModal(params)}
                >
                    {params.value}
                </Button>
                :
                <div>{params.value}</div>,
        },
        {
            field: "stonestatus",
            headerName: "STONE STATUS",
            headerAlign: "center",
            align: "center",
            width: 110,
            renderCell: renderCellExpand,
        },
        {
            field: "certificateid",
            headerName: "CERT",
            headerAlign: "center",
            align: "center",
            width: 100,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        {
            field: "stonetype",
            headerName: "STONETYPE",
            headerAlign: "center",
            align: "center",
            width: 90,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.stonetype.map(option => option.fullname),
            filterOperators: Utility.combinedFilters(),
            valueGetter: (params) => Constants.STONE_TYPE[params.value],
            renderCell: (params) => Constants.STONE_TYPE[params.value],
        },
        {
            field: "shape",
            headerName: "SHAPE",
            headerAlign: "center",
            align: "center",
            width: 80,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.shape.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        // {
        //     headerName: "PERFORMA DATE",
        //     field: "performadate",
        //     type: "date",
        //     width: 150,
        //     valueGetter: (params) => {
        //         if (params.value) {
        //             return new Date(params.value);
        //         }
        //         return null;
        //     },
        //     renderCell: (params) => Utility.ConvertUtcToLocalDate(params.row.performadate),
        //     headerAlign: "center",
        //     align: "center",
        // },
        {
            field: "size",
            headerName: "SIZE",
            headerAlign: "center",
            width: 40,
            type: "number",
            valueFormatter: (params) => params.value,
            align: "right",
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.size)
        },
        {
            field: "color",
            headerName: "COLOR",
            headerAlign: "center",
            align: "center",
            width: 65,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.color.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "clarity",
            headerName: "CLARITY",
            headerAlign: "center",
            align: "center",
            width: 75,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.clarity.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "cut",
            headerName: "CUT",
            headerAlign: "center",
            align: "center",
            width: 50,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.cut.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "pol",
            headerName: "POL",
            headerAlign: "center",
            align: "center",
            width: 40,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.pol.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "sym",
            headerName: "SYM",
            headerAlign: "center",
            align: "center",
            width: 40,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.sym.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "flo",
            headerName: "FLO",
            headerAlign: "center",
            align: "center",
            width: 60,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.flo.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "originalprice",
            headerName: "RAP",
            headerAlign: "center",
            align: "center",
            width: 70,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.value),
        },
        {
            field: "defaultdiscount",
            headerName: "DEFAULT DIS",
            headerAlign: "center",
            align: "center",
            width: 93,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.value),
        },
        {
            field: "actualdiscount",
            headerName: "ACTUAL DIS",
            headerAlign: "center",
            align: "center",
            width: 90,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.value),
        },
        {
            field: "discountprice",
            headerName: "PPC",
            headerAlign: "center",
            align: "center",
            width: 70,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.value),
        },
        {
            field: "stoneprice",
            headerName: "TOTAL",
            headerAlign: "center",
            align: "center",
            width: 70,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.value),
        },
        {
            field: "lab",
            headerName: "LAB",
            headerAlign: "center",
            align: "center",
            width: 40,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.lab.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "location",
            headerName: "LOCATION",
            headerAlign: "center",
            align: "center",
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.location.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            width: 100,
        },
        {
            field: "status",
            headerName: "INVOICE DETAIL STATUS",
            headerAlign: "center",
            align: "center",
            width: 160,
            renderCell: renderCellExpand,
        },
    ];
    const handleInvoiceDetailModal = (params) => {
        setSelectedInvoiceId(params.value);
        setShowViewSellModal(true);
    };

    useEffect(() => {
        handleSearch();
    }, []);

    const handleSearch = () => {
        let data = {
            from: Utility.ConvertLocalTimeToUtc(dayjs(invoiceDate).format('YYYY-MM-DD') + "T00:00:00.000"),
            to: Utility.ConvertLocalTimeToUtc(dayjs(invoiceDate).format('YYYY-MM-DD') + "T23:59:59.999")
        }

        setLoader(true);
        ReportProxy.GetCancelledStones(data, (response) => {
            if (response) {

                let tempResponse = [];
                response.forEach(data => {
                    let tempObj = {
                        ...data,
                        invoiceid_stoneid: data.invoiceid + "_" + data.stoneid,
                        stoneid: data.stonedetail.stoneid,
                        stonetype: data.stonedetail.stonetype,
                        size: data.stonedetail.size,
                        shape: data.stonedetail.shape,
                        color: data.stonedetail.color,
                        clarity: data.stonedetail.clarity,
                        cut: data.stonedetail.cut,
                        pol: data.stonedetail.pol,
                        sym: data.stonedetail.sym,
                        flo: data.stonedetail.flo,
                        certificateid: data.stonedetail.certificateid,
                        stonestatus: data.stonedetail.status,
                        location: data.stonedetail.location,
                        lab: data.stonedetail.lab
                    }
                    tempResponse.push(tempObj);
                });

                setData(tempResponse);
                getSummaryReport(tempResponse);
                setLoader(false);
            } else {
                setLoader(false);
                setShowSnackbar(true);
                setSnackbarMessage(Constants.APiFailErrorMessage);
                setSnackbarVariant("error");
            }
        });
    }

    const getSummaryReport = (response) => {
        let totalSize = 0;
        let totalPrice = 0;   //ppc*size
        let total = 0;       //rapnetprice*size
        response.forEach((iData) => {
            let pricePerCarat = iData.discountprice
            totalSize += iData.size;
            totalPrice += (iData.size * pricePerCarat);
            if (iData.actualdiscount !== -100)
                total += (iData.size * ((100 * pricePerCarat) / (100 + iData.actualdiscount)));
        });
        setNoOfStones(response.length);
        setTotalSize(Utility.ConvertNumberWithSpecifiedDigits(totalSize));
        setTotalPrice(Utility.ConvertNumberWithSpecifiedDigits(totalPrice));
        setAvgPrice(totalPrice > 0 ?
            Utility.ConvertNumberWithSpecifiedDigits(totalPrice / totalSize) :
            Utility.ConvertNumberWithSpecifiedDigits(0)
        );
        setAvgDiscount(totalPrice > 0 ?
            Utility.ConvertNumberWithSpecifiedDigits((100 - (totalPrice / total) * 100) * -1) :
            Utility.ConvertNumberWithSpecifiedDigits(0)
        );
    }

    const handleStoneDetailModal = (params) => {
        setStoneId(params.value);
        setShowStoneDetailModal(true);
    };

    return (
        <Box sx={{ mt: "8px" }}>

            <CommonSnackbar
                show={showSnackbar}
                message={snackbarMessage}
                variant={snackbarVariant}
                autoHide={Constants.SnackBarCloseTimer}
                title={snackbarVariant === "success" ? "Success" : snackbarVariant === "error" ? "Error" : "Warning"}
                handleClose={() => setShowSnackbar(false)}
            />

            {showStoneDetailModal ? (
                <StoneDetailModal
                    show={showStoneDetailModal}
                    stoneId={stoneId}
                    handleClose={() => setShowStoneDetailModal(false)}
                />
            ) : null}

            {showViewSellModal ? (
                <ViewSellMemoModal
                    id={selectedInvoiceId}
                    show={true}
                    setShow={setShowViewSellModal}
                    isProforma={false}
                />
            ) : null}

            {/* Table */}
            <Box
                sx={{
                    marginTop: "8px",
                    backgroundColor: '#fff',
                    color: '#000',
                    width: "100%",
                    position: "relative",
                    "& .MuiTablePagination-selectLabel": {
                        marginTop: "15px",
                    },
                    "& .MuiTablePagination-displayedRows": {
                        marginTop: "15px",
                    },
                    "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                        fontWeight: Constants.defaultFontWeight,
                    },
                    "& .css-cc8tf1": {
                        fontWeight: Constants.defaultFontWeight,
                    },
                    height: '66vh'
                }}
            >
                <DataGridPro
                    disableRowSelectionOnClick={true}
                    localeText={{ noRowsLabel: "No Record Found" }}
                    sx={{
                        '& .MuiDataGrid-columnHeader': {
                            color: '#fff',
                            backgroundColor: 'grey',
                        },
                        "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                            py: "4px",
                        },
                        "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                            py: "12px",
                        },
                        "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                            py: "18px",
                        },

                        fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
                    }}
                    density="compact"
                    slots={{ toolbar: GridToolbar }}
                    rows={data}
                    columns={columns}
                    getRowId={(row) => row.invoiceid_stoneid}
                    loading={loader}
                    initialState={{
                        columns: {
                            columnVisibilityModel: {
                                // Hide columns id, the other columns will remain visible
                            },
                        },
                        pagination: {
                            paginationModel: Constants.pageSize,
                        },
                    }}
                    pageSizeOptions={Constants.pageSizeOptions}
                    pagination
                    slotProps={{
                        toolbar: {
                            showQuickFilter: true,
                            quickFilterProps: {
                                debounceMs: 500,
                            },
                        },
                    }}
                />

                <Box
                    sx={{
                        position: "absolute",
                        top: 12,
                        right: "288px",
                        width: "55%",
                        display: 'flex',
                        justifyContent: "center",
                        alignItems: 'center',
                        color: "black",
                        fontWeight: 'normal',
                        fontSize: Constants.defaultTableFontSize
                    }}
                ><p style={{ margin: 0 }}>
                        <span>No of Stones: </span> <b>{noOfStones}</b> &nbsp;&nbsp;|&nbsp;&nbsp;
                        <span>Total Weight: </span> <b>{totalSize}</b>  &nbsp;&nbsp;|&nbsp;&nbsp;
                        <span>Total Price: </span> <b>{totalPrice}</b>  &nbsp;&nbsp;|&nbsp;&nbsp;
                        <span>Avg Price: </span> <b>{avgPrice}</b>  &nbsp;&nbsp;|&nbsp;&nbsp;
                        <span>Avg Discount: </span> <b>{avgDiscount}</b></p>
                </Box>

                <Box style={{ position: "absolute", top: 9, right: "265px", display: "flex", alignItems: "center" }}>
                    {loader ? (
                        <CircularProgress size="1rem" />
                    ) : (
                        <Tooltip title="Reload List">
                            <RefreshOutlinedIcon
                                onClick={() => handleSearch()}
                                color="primary"
                                fontSize="medium"
                                sx={{ cursor: "pointer" }}
                            />
                        </Tooltip>
                    )}
                </Box>

            </Box>

        </Box>
    );
};

export default CancelledStoneReport;